import type { Content } from "@/src/features/api/openData";
import { ContentCategoryEnum } from "@/src/features/api/openData";
import type { MarketSummaryItemData } from "@/src/components/Home/MarketSummary/types";

/**
 * 将 Content 转换为 MarketSummaryItemData
 */
const transformContentToMarketSummaryItem = (
  content: Content,
  stockType?: "HK" | "US",
): MarketSummaryItemData => {
  // 提取 description：直接使用 description 字段（纯文本）
  const description = content.description || "";

  // 判断资产类型和分类
  const isStock = content.categories?.includes(ContentCategoryEnum.STOCK);
  const category: "stock" | "crypto" = isStock ? "stock" : "crypto";

  // 提取 asset 信息：从 symbols 中提取第一个 symbol
  let asset: MarketSummaryItemData["asset"] | undefined;
  if (content.symbols && content.symbols.length > 0) {
    const symbols = content?.symbols;
    const firstSymbol = symbols?.[0];

    // 对于股票，根据 stockType(HK | US) 优先选择与 symbol.market 一致的第一个 symbol
    const targetSymbol =
      isStock && stockType
        ? (symbols?.find((symbol) => symbol.market === stockType) ??
          firstSymbol)
        : firstSymbol;

    let change = 0;
    if (isStock) {
      // 股票：使用 current_price 和 previous_close 计算涨跌幅
      // 涨跌幅（%）= (current_price - previous_close) / previous_close × 100%
      const currentPrice = targetSymbol.current_price;
      const previousClose = targetSymbol.previous_close;
      if (
        previousClose !== undefined &&
        previousClose !== 0 &&
        currentPrice !== undefined
      ) {
        change = ((currentPrice - previousClose) / previousClose) * 100;
      }
    } else {
      // 加密货币：直接使用 price_change_24h 字段
      change = firstSymbol.price_change_24h ?? 0;
    }

    // 处理异常值（NaN、Infinity 等）
    if (!isFinite(change) || isNaN(change)) {
      change = 0;
    }

    asset = {
      icon: targetSymbol.logo || "", // 待定，暂时使用 logo
      symbol: targetSymbol.symbol || "",
      name: targetSymbol.short_name || targetSymbol.symbol || "",
      change,
      id: targetSymbol.id, // 资产 ID，用于跳转详情页
      type: isStock ? "stock" : "crypto", // 资产类型
    };
  }

  return {
    ...content,
    id: content.id || "",
    title: content.title || "",
    description,
    asset,
    sourceLink: content.source_link,
    category,
  };
};

/**
 * 批量转换 Content 数组为 MarketSummaryItemData 数组
 */
export const transformContentsToMarketSummaryItems = (
  contents: Content[],
  stockType?: "HK" | "US",
): MarketSummaryItemData[] => {
  return contents
    .filter((content) => content.id && content.title) // 过滤掉无效数据
    .map((content) => transformContentToMarketSummaryItem(content, stockType));
};
